import { useEffect } from 'react';

interface UseKeyboardHandlerProps {
  handleNumberInput: (digit: string) => void;
  handleBackspace: () => void;
  handleCall: () => void;
  handleClear: () => void;
  handlePasteNumber?: (text: string) => void;
  phoneNumber: string;
}

export const useKeyboardHandler = ({
  handleNumberInput,
  handleBackspace,
  handleCall,
  handleClear,
  handlePasteNumber,
  phoneNumber,
}: UseKeyboardHandlerProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't capture keyboard events if user is typing in an input, textarea, or contenteditable element
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const key = event.key;

      // Handle number and symbol keys
      if (/[0-9*#]/.test(key)) {
        event.preventDefault();
        handleNumberInput(key);
      }
      // Handle plus key
      else if (key === '+') {
        event.preventDefault();
        handleNumberInput('+');
      }
      // Handle backspace
      else if (key === 'Backspace') {
        event.preventDefault();
        handleBackspace();
      }
      // Handle enter for call
      else if (key === 'Enter' && phoneNumber) {
        event.preventDefault();
        handleCall();
      }
      // Handle escape for clear
      else if (key === 'Escape') {
        event.preventDefault();
        handleClear();
      }
    };

    const handlePaste = (event: ClipboardEvent) => {
      if (!handlePasteNumber) return;

      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const text = event.clipboardData?.getData('text/plain');
      if (text) {
        event.preventDefault();
        handlePasteNumber(text);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('paste', handlePaste);
    };
  }, [handleNumberInput, handleBackspace, handleCall, handleClear, handlePasteNumber, phoneNumber]);
};
